import { Combobox, Spinner, Box, Badge, Text } from "@adfenix/alexandria";
import { ProgramService } from "@adfenix/types";
import { getCanTriggerPrograms } from "api/services/programService";
import { Controller, useFormContext } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import styled from "styled-components";
import { useDisabledFields } from "./useDisabledFields";
import { produce } from "immer";
import { useTranslation } from "react-i18next";

const GridBox = styled(Box)`
  display: grid;
  row-gap: 8px;
  max-height: 300px;
  overflow: scroll;
`;

const StyledText = styled(Text)`
  align-self: center;
`;

export const TriggerByPrograms = () => {
  const { t } = useTranslation();

  const disabledFields = useDisabledFields();
  const { watch, setValue } = useFormContext<ProgramService.ProgramModel>();
  const { hqId } = useParams<string>();

  const { data: allProgramsThatCanTriggerPrograms } = useQuery(
    ["getCanTriggerPrograms", hqId],
    () => getCanTriggerPrograms(hqId!, "Property"),
    { enabled: Boolean(hqId) }
  );

  const { triggerByPrograms } = watch();

  if (!allProgramsThatCanTriggerPrograms) {
    return (
      <Box>
        <Spinner size={20} />
      </Box>
    );
  }

  const selectablePrograms = allProgramsThatCanTriggerPrograms.filter(x =>
    triggerByPrograms?.every(y => y.id !== x.id)
  );

  return (
    <>
      <Controller
        name="triggerByPrograms"
        render={({ field }) => (
          <Combobox
            items={selectablePrograms}
            valueObjectIndexAccessor="name"
            placeholder={t(
              "programBuilder.program.details.triggerByPrograms.placeholder"
            )}
            disabled={
              !selectablePrograms ||
              selectablePrograms.length === 0 ||
              disabledFields("triggerByPrograms")
            }
            onChange={item => {
              const updatedSelectedItems = produce(
                triggerByPrograms ?? [],
                draft => {
                  draft.push(item);
                }
              );
              field.onChange(updatedSelectedItems);
            }}
            label={t("programBuilder.program.details.triggerByPrograms.label")}
          />
        )}
      />

      <GridBox pY="xs">
        {triggerByPrograms &&
          triggerByPrograms.map(program => {
            let programThatWillTriggerProgram =
              allProgramsThatCanTriggerPrograms.find(x => x.id === program.id);

            if (!programThatWillTriggerProgram) {
              programThatWillTriggerProgram = {
                id: program.id,
                name: program.id
              };
            }

            return (
              <Badge color="primary" key={programThatWillTriggerProgram.id}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  style={{ height: "100%" }}
                >
                  <Link
                    to={`/back-office/hq/${hqId}/program-builder/${programThatWillTriggerProgram.id}`}
                  >
                    {programThatWillTriggerProgram.name}
                  </Link>
                  <StyledText>
                    {!disabledFields("triggerByPrograms") && (
                      <i
                        className="uil uil-times"
                        onClick={() => {
                          setValue(
                            "triggerByPrograms",
                            triggerByPrograms?.filter(
                              program =>
                                program.id !== programThatWillTriggerProgram!.id
                            ),
                            { shouldDirty: true }
                          );
                        }}
                      />
                    )}
                  </StyledText>
                </Box>
              </Badge>
            );
          })}
      </GridBox>
    </>
  );
};
